'use client';

import { type FC, useCallback, useState } from 'react';
import { submitQuestionnaire } from '@/app/apis';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { CANCEL_REASON_OPTIONS } from '../constants';

const OTHER_REASON_VALUE = '其他原因';

export interface CancelReasonQuestionnaireProps {
  open: boolean;
  onClose?: () => void;
}

export const CancelReasonQuestionnaire: FC<CancelReasonQuestionnaireProps> = ({ open, onClose }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [otherInput, setOtherInput] = useState('');

  const isOtherSelected = selected.includes(OTHER_REASON_VALUE);

  const { run: submit, loading } = useFetch(
    (cancelReason: string[]) =>
      submitQuestionnaire({
        body: {
          questionnaire_type: 'cancel_auto_renew_reason',
          cancel_reason: cancelReason,
        },
      }),
    {
      manual: true,
      onSuccess: () => {
        toast({ type: 'success', message: '感谢反馈，我们会为你优化体验' });
        onClose?.();
      },
    },
  );

  const handleToggle = useCallback((value: string) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }, []);

  const handleSubmit = () => {
    if (selected.length === 0) {
      toast({ type: 'warning', message: '请至少选择一个选项' });
      return;
    }
    const reasons = selected.map((v) =>
      v === OTHER_REASON_VALUE && otherInput.trim() ? `其他原因：${otherInput.trim()}` : v,
    );
    submit(reasons);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose?.();
      }}
    >
      <DialogContent className="w-[630px] gap-0 p-0 sm:w-full sm:gap-0 sm:p-0 md:max-w-[630px] md:gap-0 md:p-0">
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 bg-[linear-gradient(180deg,#333b4d00_0%,#333b4d5c_100%)] px-6 pt-10 pb-2">
          <div className="font-semibold text-lg text-system-white">反馈取消原因</div>
          <div className="text-center text-sm text-system-white opacity-60">填写调查问卷，领取奖励</div>
        </div>

        <div className="px-6 pt-4 pb-2">
          <div className="mb-4 text-sm text-system-white">你这次取消自动续费的主要原因是什么？（可多选）</div>
          <div className="flex flex-row flex-wrap gap-3">
            {CANCEL_REASON_OPTIONS.map((option) => (
              <div
                key={option.value}
                className={cn(
                  'flex h-[52px] w-[calc(50%-6px)] cursor-pointer items-center justify-center rounded-8 bg-[#ffffff14] px-4 text-center text-sm text-system-white transition-colors md:w-[calc(50%-6px)]',
                  selected.includes(option.value) && 'bg-[#0086FF] text-white',
                )}
                onClick={() => handleToggle(option.value)}
              >
                {option.label}
              </div>
            ))}
          </div>
          {isOtherSelected && (
            <Input
              className="mt-3 h-[52px] rounded-8 border-none bg-[#ffffff0a] py-3 pl-6"
              value={otherInput}
              onChange={(e) => setOtherInput(e.target.value)}
              placeholder="请输入其他原因"
              maxLength={50}
            />
          )}
        </div>

        <div className="flex justify-center px-6 pt-4 pb-6">
          <Button
            variant="primary"
            className="h-11 w-full"
            loading={loading}
            disabled={selected.length === 0}
            onClick={handleSubmit}
          >
            提交
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
